(function () {
  if (window.ElxrCarouselNav) return;

  const noop = { update() {}, destroy() {} };
  const DARK_CLASS = 'elxr-carousel-on-dark';
  const COLOR_VARS = ['--carousel-dot-color', '--carousel-dot-active-color', '--carousel-progress-track', '--carousel-progress-fill'];

  function parseColors(value) {
    const colors = [];
    const re = /rgba?\(([^)]+)\)/g;
    let match;
    while ((match = re.exec(value))) {
      const parts = match[1].split(/[\s,/]+/).filter(Boolean).map(parseFloat);
      if (parts.length >= 3) colors.push({ r: parts[0], g: parts[1], b: parts[2], a: parts.length > 3 ? parts[3] : 1 });
    }
    return colors;
  }

  function luminance({ r, g, b }) {
    const channel = (c) => {
      const v = c / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  }

  // Walks up to the first element that actually paints a background (solid colour or gradient).
  function hasDarkBackground(el) {
    for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (style.backgroundImage.includes('gradient')) {
        const stops = parseColors(style.backgroundImage).filter((c) => c.a >= 0.5);
        if (stops.length) return stops.reduce((sum, c) => sum + luminance(c), 0) / stops.length < 0.179;
      }
      const [bg] = parseColors(style.backgroundColor);
      if (bg && bg.a >= 0.5) return luminance(bg) < 0.179;
    }
    return false;
  }

  function autoContrastEnabled() {
    return getComputedStyle(document.documentElement).getPropertyValue('--carousel-auto-contrast').trim() !== '0';
  }

  // Sections in Custom navigation mode override the colour variables; leave those alone.
  function usesGlobalColors(el) {
    const own = getComputedStyle(el.parentElement || el);
    const root = getComputedStyle(document.documentElement);
    return COLOR_VARS.every((name) => own.getPropertyValue(name).trim() === root.getPropertyValue(name).trim());
  }

  function applyContrast(el, { requireGlobalColors = true } = {}) {
    if (!el) return;
    el.classList.remove(DARK_CLASS);
    if (!autoContrastEnabled()) return;
    if (requireGlobalColors && !usesGlobalColors(el)) return;
    if (hasDarkBackground(el)) el.classList.add(DARK_CLASS);
  }

  function applyContrastWithin(scope) {
    (scope || document).querySelectorAll('[data-carousel-contrast]').forEach((el) => {
      applyContrast(el, { requireGlobalColors: false });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyContrastWithin(document));
  } else {
    applyContrastWithin(document);
  }
  document.addEventListener('shopify:section:load', (event) => applyContrastWithin(event.target));

  class CarouselIndicators {
    constructor(el, options) {
      this.el = el;
      this.onGoTo = typeof options.onGoTo === 'function' ? options.onGoTo : null;
      this.dots = el.querySelector('[data-carousel-dots]');
      this.progress = el.querySelector('[data-carousel-progress]');
      this.fill = this.progress ? this.progress.querySelector('.elxr-carousel-progress__fill') : null;
      this.total = 0;
      this.index = 0;
      this.dotButtons = [];
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      if (this.progress && this.onGoTo) this.progress.addEventListener('pointerdown', this.onPointerDown);
      applyContrast(el);
    }

    update(index, total) {
      const count = Math.max(1, Math.round(Number(total) || 1));
      const current = Math.min(count - 1, Math.max(0, Math.round(Number(index) || 0)));
      this.el.hidden = count <= 1;

      if (this.dots && count !== this.total) this.buildDots(count);
      this.total = count;
      this.index = current;

      this.dotButtons.forEach((dot, i) => {
        const active = i === current;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (this.fill) this.fill.style.width = `${((current + 1) / count) * 100}%`;
    }

    buildDots(count) {
      this.dots.textContent = '';
      this.dotButtons = [];
      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'elxr-carousel-dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => {
          if (this.onGoTo) this.onGoTo(i);
        });
        this.dots.appendChild(dot);
        this.dotButtons.push(dot);
      }
    }

    indexFromPointer(event) {
      const rect = this.progress.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      return Math.min(this.total - 1, Math.floor(ratio * this.total));
    }

    onPointerDown(event) {
      if (this.total <= 1) return;
      this.progress.classList.add('is-seeking');
      this.progress.setPointerCapture?.(event.pointerId);
      this.progress.addEventListener('pointermove', this.onPointerMove);
      this.progress.addEventListener('pointerup', this.onPointerUp);
      this.progress.addEventListener('pointercancel', this.onPointerUp);
      this.seek(event);
    }

    onPointerMove(event) {
      this.seek(event);
    }

    onPointerUp() {
      this.progress.classList.remove('is-seeking');
      this.progress.removeEventListener('pointermove', this.onPointerMove);
      this.progress.removeEventListener('pointerup', this.onPointerUp);
      this.progress.removeEventListener('pointercancel', this.onPointerUp);
    }

    seek(event) {
      const target = this.indexFromPointer(event);
      if (target !== this.index) this.onGoTo(target);
    }

    destroy() {
      if (this.progress) this.progress.removeEventListener('pointerdown', this.onPointerDown);
      if (this.dots) this.dots.textContent = '';
    }
  }

  function findIndicators(scope) {
    if (!scope) return null;
    if (scope.matches && scope.matches('[data-carousel-indicators]')) return scope;
    return scope.querySelector('[data-carousel-indicators]');
  }

  function create(scope, options = {}) {
    const el = findIndicators(scope);
    return el ? new CarouselIndicators(el, options) : noop;
  }

  function bindSplide(scope, splide) {
    if (!splide) return noop;
    let stops = [];
    const nav = create(scope, { onGoTo: (i) => splide.go(stops.length ? stops[i] : i) });
    if (nav === noop) return noop;

    // Splide can report several indexes that rest at the same scroll position
    // (focus/trimSpace edges), so dots map to distinct positions, not raw indexes.
    const measureStops = () => {
      const { Controller, Move } = splide.Components;
      const end = Controller.getEnd();
      stops = [];
      let last = null;
      for (let i = 0; i <= end; i++) {
        const pos = Move && Move.toPosition ? Math.round(Move.toPosition(i, true)) : i;
        if (pos !== last) stops.push(i);
        last = pos;
      }
    };

    const sync = (index) => {
      const isLoop = splide.options && splide.options.type === 'loop';
      const current = typeof index === 'number' ? index : splide.index;
      if (isLoop) {
        nav.update(((current % splide.length) + splide.length) % splide.length, splide.length);
        return;
      }
      if (!stops.length) measureStops();
      let stop = 0;
      while (stop + 1 < stops.length && stops[stop + 1] <= current) stop++;
      nav.update(stop, stops.length);
    };

    splide.on('mounted resized refresh updated', () => {
      stops = [];
    });

    splide.on('move', (newIndex) => sync(newIndex));
    splide.on('mounted resized refresh updated', () => sync());
    if (splide.state && splide.state.is && !splide.state.is(1)) sync();
    return nav;
  }

  function bindScroll(scope, scroller, options = {}) {
    if (!scroller) return noop;
    const getStep = () => {
      const item = options.itemSelector ? scroller.querySelector(options.itemSelector) : scroller.firstElementChild;
      if (!item) return scroller.clientWidth || 1;
      const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
      return item.getBoundingClientRect().width + gap || 1;
    };
    const nav = create(scope, {
      onGoTo: (i) => scroller.scrollTo({ left: i * getStep(), behavior: 'smooth' }),
    });
    if (nav === noop) return noop;

    const sync = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      if (max <= 1) return nav.update(0, 1);
      const step = getStep();
      const total = Math.round(max / step) + 1;
      const index = scroller.scrollLeft >= max - 1 ? total - 1 : Math.round(scroller.scrollLeft / step);
      nav.update(index, total);
    };

    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    };
    scroller.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    sync();
    return nav;
  }

  window.ElxrCarouselNav = { create, bindSplide, bindScroll, applyContrast };
})();
